import { clients } from '@moovly/clients';
import { AccessUserResponse } from '@moovly/clients/lib/resources/access/access.interfaces';
import { GroupInfo } from '@moovly/clients/lib/resources/group/group.interfaces';
import { Organization } from '@moovly/clients/lib/resources/organization/organization.interface';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DefaultApiRequestState, getDefaultApiRequestState, getDefaultReducerForAsyncRequest } from './default';

type OrganizationState = { groups: GroupInfo[]; info: Organization };

export const getUserAccess = createAsyncThunk('acccess/getUserAccess', () => {
  return clients.Access.getUserMe();
});

export const getGroupsOrganization = createAsyncThunk<
  OrganizationState,
  { groups: string[]; organizationId: string | null }
>('acccess/getGroups', ({ groups, organizationId }) => {
  const promises: any = [
    Promise.all(
      groups.map(
        (gr) =>
          new Promise((resolve) => {
            clients.Group.getGroup(gr)
              .then(resolve)
              .catch(() => resolve(null));
          })
      )
    ),
  ];
  if (organizationId) {
    promises.push(clients.Organization.getOrganization(organizationId));
  }
  return Promise.all(promises).then(([groups, organization]) => {
    return {
      groups: groups.filter((gr: any) => !!gr),
      info: organization && {
        ...organization,
        groups: organization.groups.filter((gr: GroupInfo) => !gr.archived),
      },
    };
  });
});

type AccessState = {
  userAccess: DefaultApiRequestState<AccessUserResponse>;
  organization: DefaultApiRequestState<OrganizationState>;
  isAllowed: boolean;
};

const initialState: AccessState = {
  userAccess: getDefaultApiRequestState(),
  organization: getDefaultApiRequestState(),
  isAllowed: true,
};

export const accessSlice = createSlice({
  name: 'access',
  initialState,
  reducers: {
    reset: () => initialState,
    isAllowed: (state, { payload }: PayloadAction<boolean>) => {
      state.isAllowed = payload;
    },
    addGroup: (state, { payload }: PayloadAction<GroupInfo>) => {
      if (state.organization.data?.info.groups) {
        state.organization.data?.info.groups.push(payload);
      } else {
        state.organization.data?.groups.push(payload);
      }
    },
  },
  extraReducers: {
    ...getDefaultReducerForAsyncRequest({
      thunk: getUserAccess,
      getInitialState: (state) => state.userAccess,
    }),
    ...getDefaultReducerForAsyncRequest({
      thunk: getGroupsOrganization,
      getInitialState: (state) => state.organization,
    }),
  },
});

export default accessSlice;
